import React, { useState, useRef, useEffect } from 'react';
import { MapPin, ChevronDown } from 'lucide-react';
import { WILAYAS, searchWilayas, formatWilaya, normalizeString } from '../../constants/wilayas';
import { cn } from '../../lib/utils';
import { useClickOutside } from '../../hooks/useClickOutside';

interface WilayaSelectorProps {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onWilayaSelect?: (wilayaCode: string) => void; // Callback quand une wilaya est sélectionnée
  placeholder?: string;
  className?: string;
}

export const WilayaSelector: React.FC<WilayaSelectorProps> = ({
  name,
  value,
  onChange,
  onWilayaSelect,
  placeholder = "Sélectionner une wilaya",
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWilayas, setFilteredWilayas] = useState([]); // Initialement vide
  const [lastSelectedValue, setLastSelectedValue] = useState(''); // Dernière valeur sélectionnée
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setIsOpen(false), [inputRef.current]);

  // Mettre à jour les suggestions uniquement si 2 caractères ou plus sont saisis
  useEffect(() => {
    // Si la valeur actuelle est la même que la dernière sélection, ne rien faire
    if (searchTerm === lastSelectedValue) {
      return;
    }
    
    if (searchTerm.trim().length >= 2) {
      const results = searchWilayas(searchTerm);
      setFilteredWilayas(results.slice(0, 10)); // Limiter à 10 résultats
      setIsOpen(true); // Ouvrir le dropdown quand il y a des résultats
    } else {
      setFilteredWilayas([]); // Ne rien afficher si moins de 2 caractères
      setIsOpen(false); // Fermer le dropdown
    }
  }, [searchTerm, lastSelectedValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);

    // Simuler un événement pour le parent
    onChange({
      target: {
        name,
        value: inputValue,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleWilayaSelect = (wilaya: typeof WILAYAS[number]) => {
    const formattedValue = formatWilaya(wilaya);
    
    setSearchTerm(formattedValue);
    setLastSelectedValue(formattedValue); // Mémoriser la valeur sélectionnée
    
    setIsOpen(false);
    setFilteredWilayas([]);
    

    // Simuler un événement pour le parent
    onChange({
      target: {
        name,
        value: formattedValue,
        type: 'text'
      }
    } as React.ChangeEvent<HTMLInputElement>);

    // Notifier la sélection de wilaya pour charger les communes
    if (onWilayaSelect) {
      onWilayaSelect(wilaya.code);
    }
  };

  const handleInputFocus = () => {
    // Ne pas afficher la liste complète au clic (seulement si searchTerm a 2+ caractères et différent de la dernière sélection)
    if (searchTerm.trim().length >= 2 && searchTerm !== lastSelectedValue) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown' && filteredWilayas.length > 0) {
      e.preventDefault();
      // Focus sur le premier élément de la liste
      const firstOption = dropdownRef.current?.querySelector('button');
      firstOption?.focus();
    }
  };

  // Synchroniser searchTerm avec value du parent uniquement au montage
  useEffect(() => {
    if (value && !searchTerm) {
      setSearchTerm(value);
      setLastSelectedValue(value); // Mémoriser aussi comme dernière sélection
    }
  }, [value]);
  
  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={searchTerm || value}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm",
            "pl-10 pr-3 touch-manipulation",
            className
          )}
          autoComplete="off"
        />
        <MapPin className="absolute left-3 top-1/2 h-5 w-5 sm:h-4 sm:w-4 -translate-y-1/2 text-gray-400" />
      </div>
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800 scrollbar-hide"
        >
          {filteredWilayas.length > 0 ? (
            filteredWilayas.map((wilaya) => (
              <button
                key={wilaya.code}
                type="button"
                onClick={() => handleWilayaSelect(wilaya)}
                className="flex w-full items-center gap-3 px-4 py-3 sm:py-2 text-left text-base sm:text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none dark:hover:bg-gray-700 dark:focus:bg-gray-700 touch-manipulation"
              >
                <span className="font-mono text-sm sm:text-xs text-gray-500 dark:text-gray-400">
                  {wilaya.code}
                </span>
                <span className="text-gray-900 dark:text-white text-base sm:text-sm">
                  {wilaya.name}
                </span>
              </button>
            ))
          ) : (
            searchTerm.trim().length >= 2 && (
              <div className="px-4 py-3 sm:py-2 text-base sm:text-sm text-gray-500 dark:text-gray-400">
                Aucune wilaya trouvée
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};
